// ─── Lecture défensive des variables d'environnement ────────────────────
//
// Créé le 2026-08-01 après l'incident du retour à la ligne.
//
// CE QUI S'EST PASSÉ. La variable VITE_SUPABASE_ANON_KEY de Vercel se
// terminait par un `\n` (clé collée avec un retour à la ligne). Vite l'a
// inlinée telle quelle. Les appels REST passaient en 200 — la clé y voyage
// dans un EN-TÊTE HTTP, où l'espace final est toléré — mais le websocket
// Realtime met la clé dans la QUERY STRING, où le `\n` devient `%0A` : la
// passerelle refusait avec 401 UNAUTHORIZED_MISSING_API_KEY. Résultat, un
// cockpit parfaitement fonctionnel dont le temps réel était mort, sans
// aucun message explicite. Des heures de diagnostic pour un caractère.
//
// RÈGLE POSÉE PAR ANISSA (2026-08-01) :
//  - espaces en début/fin : retirés, avec un avertissement ;
//  - valeur vide après détourage : REFUSÉE ;
//  - caractère blanc INTERNE : REFUSÉ, pas corrigé en silence — une clé
//    coupée en deux par un espace est une clé fausse, la rafistoler
//    masquerait le vrai problème.
//
// L'avertissement nomme la variable, JAMAIS sa valeur.

export const ETAT_VARIABLE = Object.freeze({
  OK: 'ok',
  ABSENTE: 'absente',
  VIDE: 'vide',
  ESPACE_INTERNE: 'espace_interne',
});

/**
 * Normalise une variable d'environnement, ou la refuse.
 *
 * @param {string} nom - nom de la variable, pour le message
 * @param {unknown} brut - valeur telle que fournie par l'environnement
 * @returns {{ valeur: string|null, etat: string, detourageApplique: boolean, message: string }}
 */
export function normaliserVariableEnv(nom, brut) {
  if (typeof brut !== 'string') {
    return {
      valeur: null,
      etat: ETAT_VARIABLE.ABSENTE,
      detourageApplique: false,
      message: `${nom} est absente de l'environnement.`,
    };
  }

  const detouree = brut.trim();
  const detourageApplique = detouree !== brut;

  if (detouree === '') {
    return {
      valeur: null,
      etat: ETAT_VARIABLE.VIDE,
      detourageApplique,
      message: `${nom} est vide (ou ne contient que des espaces).`,
    };
  }

  // Un espace, une tabulation ou un saut de ligne AU MILIEU de la valeur ne
  // peut pas être un accident bénin : on refuse plutôt que de deviner.
  if (/\s/.test(detouree)) {
    return {
      valeur: null,
      etat: ETAT_VARIABLE.ESPACE_INTERNE,
      detourageApplique,
      message: `${nom} contient un caractère blanc interne — valeur refusée, `
        + 'elle n\'est pas corrigée en silence.',
    };
  }

  return {
    valeur: detouree,
    etat: ETAT_VARIABLE.OK,
    detourageApplique,
    message: detourageApplique
      ? `${nom} contenait des espaces ou un retour à la ligne en trop — valeur `
        + 'détourée. À corriger dans les variables d\'environnement : le websocket '
        + 'Realtime rejette une clé non détourée.'
      : '',
  };
}
